import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import apiClient from '@/lib/apiClient'
import { cn } from '@/lib/utils'
import { formatIDR } from '@/lib/utils'
import { SalesData } from '@/types/api'
import { useQuery } from '@tanstack/react-query'
import React, { useState } from 'react'

interface SalesChartProps {
  className?: string
}

type PeriodType = '7D' | '1M' | '3M' | '1Y'

const periodToDays: Record<PeriodType, number> = {
  '7D': 7,
  '1M': 30,
  '3M': 90,
  '1Y': 365,
}

const periodToGroupBy: Record<PeriodType, 'daily' | 'weekly' | 'monthly'> = {
  '7D': 'daily',
  '1M': 'daily',
  '3M': 'weekly',
  '1Y': 'monthly',
}

export const SalesChart: React.FC<SalesChartProps> = ({ className }) => {
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodType>('1Y')
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)

  const {
    data: response,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['salesData', selectedPeriod],
    queryFn: async () => {
      const params = {
        period: periodToDays[selectedPeriod],
        group_by: periodToGroupBy[selectedPeriod],
      }
      const response = await apiClient.getSalesData(params)
      return response
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  })

  const salesData = React.useMemo(() => response?.data || [], [response?.data])

  // Transform API data to chart format
  const chartData = React.useMemo(() => {
    if (!salesData || salesData.length === 0) return []

    return salesData.map((item: SalesData) => {
      let label = ''

      if (selectedPeriod === '1Y') {
        // For yearly view, format as month name
        const date = new Date(item.date)
        label = date.toLocaleDateString('en-US', { month: 'short' })
      } else if (selectedPeriod === '3M') {
        // For quarterly view, format as week
        // Handle "YYYY-Www" format
        if (item.date.includes('-W')) {
          const [, week] = item.date.split('-W')
          label = `W${week}`
        } else {
          // Fallback for other formats
          const date = new Date(item.date)
          label = `W${Math.ceil((date.getDate() + new Date(date.getFullYear(), date.getMonth(), 1).getDay()) / 7)}`
        }
      } else {
        // For daily view, format as day
        const date = new Date(item.date)
        label = date.toLocaleDateString('en-US', { day: 'numeric', month: 'short' })
      }

      return {
        month: label,
        sales: item.revenue,
        orders: item.orders,
      }
    })
  }, [salesData, selectedPeriod])

  const maxSales = chartData.length > 0 ? Math.max(...chartData.map((d) => d.sales)) : 0
  const totalRevenue = chartData.reduce((sum, item) => sum + item.sales, 0)
  const totalOrders = chartData.reduce((sum, item) => sum + item.orders, 0)

  // Use 120% of max sales for better visualization
  const chartMax = maxSales > 0 ? maxSales * 1.2 : 0
  const chartLevels = [1, 0.66, 0.33, 0]

  // Format y-axis labels
  const formatYAxisLabel = (value: number) => {
    // Convert to millions for display
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(0)}jt`
    }
    return formatIDR(value, { withSymbol: false })
  }

  if (error) {
    return (
      <Card className={cn('rounded-3xl border-slate-200/80 bg-white shadow-sm', className)}>
        <CardHeader className="border-b border-slate-100 pb-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <CardTitle className="text-lg font-semibold text-slate-950">Sales Overview</CardTitle>
              <CardDescription className="mt-1 text-sm text-slate-500">
                Revenue trend across the selected period.
              </CardDescription>
            </div>
            <div className="inline-flex rounded-full border border-slate-200 bg-slate-50 p-1">
              {(['7D', '1M', '3M', '1Y'] as PeriodType[]).map((period) => (
                <Button
                  key={period}
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedPeriod(period)}
                  className={cn(
                    'rounded-full px-4 text-xs font-semibold',
                    selectedPeriod === period
                      ? 'bg-slate-900 text-white hover:bg-slate-900 hover:text-white'
                      : 'text-slate-600 hover:bg-white hover:text-slate-900'
                  )}
                >
                  {period}
                </Button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="flex h-[420px] w-full items-center justify-center rounded-2xl border border-dashed border-red-200 bg-red-50/60 text-sm text-red-700">
            Failed to load sales data
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card
      className={cn(
        'flex h-full flex-col rounded-3xl border-slate-200/80 bg-white shadow-sm',
        className
      )}
    >
      <CardHeader className="border-b border-slate-100 pb-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <CardTitle className="text-lg font-semibold text-slate-950">Sales Overview</CardTitle>
            <CardDescription className="mt-1 text-sm text-slate-500">
              Revenue trend for non-cancelled orders across the selected period.
            </CardDescription>
          </div>
          <div className="flex flex-col gap-3 sm:items-end">
            <div className="inline-flex rounded-full border border-slate-200 bg-slate-50 p-1">
              {(['7D', '1M', '3M', '1Y'] as PeriodType[]).map((period) => (
                <Button
                  key={period}
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedPeriod(period)}
                  className={cn(
                    'rounded-full px-4 text-xs font-semibold',
                    selectedPeriod === period
                      ? 'bg-slate-900 text-white hover:bg-slate-900 hover:text-white'
                      : 'text-slate-600 hover:bg-white hover:text-slate-900'
                  )}
                >
                  {period}
                </Button>
              ))}
            </div>
            {!isLoading && chartData.length > 0 && (
              <div className="flex flex-wrap gap-2 text-xs text-slate-600">
                <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1">
                  Revenue {formatIDR(totalRevenue)}
                </span>
                <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1">
                  {totalOrders} orders
                </span>
              </div>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col pt-6">
        <div className="flex h-full min-h-[420px] w-full flex-col">
          {isLoading ? (
            <div className="flex h-full flex-col space-y-4">
              <Skeleton className="h-full min-h-[340px] w-full rounded-2xl" />
              <div className="flex justify-between">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <Skeleton key={i} className="h-4 w-8" />
                ))}
              </div>
            </div>
          ) : chartData.length > 0 ? (
            <div className="relative h-full min-h-[380px] overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-b from-slate-50 to-white px-4 py-4">
              <div className="absolute inset-x-4 bottom-10 left-16 top-4">
                {chartLevels.map((level) => (
                  <div
                    key={level}
                    className="absolute left-0 right-0 border-t border-dashed border-slate-200"
                    style={{ bottom: `${level * 100}%` }}
                  />
                ))}
              </div>

              <div className="absolute inset-x-4 bottom-10 left-16 top-4 flex items-end gap-3">
                {chartData.map((data, index) => {
                  const barHeight = chartMax > 0 ? (data.sales / chartMax) * 100 : 0
                  return (
                    <div
                      key={index}
                      className="group relative flex h-full flex-1 flex-col items-center justify-end"
                      onMouseEnter={() => setHoveredIndex(index)}
                      onMouseLeave={() => setHoveredIndex(null)}
                    >
                      <div
                        className="w-full rounded-t-2xl bg-blue-600 shadow-[0_14px_30px_-18px_rgba(37,99,235,0.35)] transition-all duration-300 group-hover:bg-blue-700"
                        style={{
                          height: `${Math.max(barHeight, 3)}%`,
                        }}
                      />
                      {hoveredIndex === index && (
                        <div className="absolute bottom-full z-10 mb-3 rounded-xl bg-slate-950 px-3 py-2 text-xs text-white shadow-xl">
                          <div className="font-semibold">{data.month}</div>
                          <div>{formatIDR(data.sales)}</div>
                          <div className="text-slate-300">
                            {data.orders} order{data.orders > 1 ? 's' : ''}
                          </div>
                          <div className="absolute left-1/2 top-full h-0 w-0 -translate-x-1/2 border-l-4 border-r-4 border-t-4 border-transparent border-t-slate-950"></div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>

              <div className="pointer-events-none absolute bottom-10 left-0 top-4 flex flex-col justify-between text-xs text-slate-400">
                <span>{formatYAxisLabel(chartMax)}</span>
                <span>{formatYAxisLabel(chartMax * 0.66)}</span>
                <span>{formatYAxisLabel(chartMax * 0.33)}</span>
                <span>Rp0</span>
              </div>

              <div className="absolute inset-x-4 bottom-0 left-16 flex gap-3">
                {chartData.map((data, index) => (
                  <div key={`${data.month}-${index}`} className="flex flex-1 justify-center">
                    <span className="text-xs font-medium text-slate-500">{data.month}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex h-full items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-slate-50 text-sm text-slate-500">
              No sales data available for the selected period
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
